<script setup lang="ts">
import { computed, watch } from 'vue'

import { useImageErrorHandling, useImageGallery } from '@/helpers/imageComposables'

import { errorTracker } from '@/services/errorTracking'

import type { GalleryImage } from '@/types/images'

interface Props {
  images: GalleryImage[]
}

const props = defineProps<Props>()

// Limit to 5 images and warn if more provided
const maxImages = 5

const limitedImages = computed(() => props.images.slice(0, maxImages))

watch(
  () => props.images.length,
  (count) => {
    if (count > maxImages) {
      errorTracker.track('ImageBento: Received too many images', { count, maxImages })
    }
  },
  { immediate: true },
)

const { validImages, handleImageError } = useImageErrorHandling(() => limitedImages.value)

const { noCroppingAllowed } = useImageGallery(validImages)
</script>

<template>
  <div
    v-if="validImages.length > 0"
    aria-label="Image gallery"
    class="image-bento"
    :class="{ 'no-crop': noCroppingAllowed }"
    role="group"
  >
    <div v-for="image in validImages" :key="image.src" class="bento-item">
      <img :alt="image.alt" loading="lazy" :src="image.src" @error="handleImageError(image)" />
    </div>
  </div>
</template>

<style scoped>
.image-bento {
  /* Local Themable Variables */
  --_border-radius: var(--image-bento-border-radius, var(--image-border-radius));
  --_background-color: var(--image-bento-background-color, var(--image-background-color));

  display: grid;
  width: 100%;
  height: 400px;
  gap: var(--space-0-5);

  &.no-crop {
    .bento-item {
      border-radius: 0;

      img {
        object-fit: contain;
        height: auto;
      }
    }
  }

  .bento-item {
    position: relative;
    overflow: hidden;
    border-radius: var(--_border-radius);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      background-color: var(--_background-color);
    }
  }

  /* One image */
  &:has(.bento-item:nth-last-child(1)) {
    grid-template-columns: 1fr;
  }

  /* Two images */
  &:has(.bento-item:nth-last-child(2)) {
    grid-template-columns: 3fr 1fr;
    grid-template-rows: 1fr;
  }

  /* Three images */
  &:has(.bento-item:nth-last-child(3)) {
    grid-template-columns: 3fr 1fr;
    grid-template-rows: 1fr 1fr;

    .bento-item:nth-child(1) {
      grid-row: 1 / -1;
    }
  }

  /* Four images */
  &:has(.bento-item:nth-last-child(4)) {
    grid-template-columns: 3fr 1fr;
    grid-template-rows: repeat(3, 1fr);

    .bento-item:nth-child(1) {
      grid-row: 1 / -1;
    }
  }

  /* Five images */
  &:has(.bento-item:nth-last-child(5)) {
    grid-template-columns: 3fr 1fr 2fr;
    grid-template-rows: 1fr 1fr;

    .bento-item:nth-child(1) {
      grid-row: 1 / -1;
    }
  }
}
</style>
